import { useEffect, useRef } from "react";

const images = {
  hero: "/assets/images/e-city-inauguration/Header.jpg",
  prayer: "/assets/images/e-city-inauguration/prayer.jpg",
  ribbonCutting: "/assets/images/e-city-inauguration/Header-2.jpg",
  children: "/assets/images/e-city-inauguration/6.jpg",
  community: "/assets/images/e-city-inauguration/Community.jpg",
  hope: "/assets/images/e-city-inauguration/Music-2.jpg",
  worship: "/assets/images/e-city-inauguration/Music-1.jpg",
};

const pullQuotes = [
  "This new center represents not just an expansion of physical space, but a deepening of purpose.",
  "Ray of Hope exists not to generate profit, but to serve, love, and walk alongside those in need.",
  "A place where children are nurtured, families are supported, and hope is renewed.",
];

export default function RHCABlogPost() {
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRefs.current.forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Inter:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        body {
          background: #FEF9F5;
          color: #2C2C2C;
          font-family: 'Inter', sans-serif;
          line-height: 1.8;
        }

        .blog-wrap {
          background: #FEF9F5;
          min-height: 100vh;
        }

        /* ── Hero ── */
        .hero {
          position: relative;
          height: 92vh;
          min-height: 560px;
          overflow: hidden;
          display: flex;
          align-items: flex-end;
        }

        .hero img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center 30%;
          filter: brightness(0.55);
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(27, 40, 50, 0.85) 0%, transparent 60%);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          padding: 0 clamp(1.5rem, 8vw, 9rem) 4rem;
          max-width: 920px;
        }

        .hero-tag {
          display: inline-block;
          font-family: 'Inter', sans-serif;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #D4A54A;
          border: 1px solid #D4A54A;
          padding: 6px 14px;
          border-radius: 2px;
          margin-bottom: 1.2rem;
        }

        .hero h1 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2.2rem, 5.5vw, 4.2rem);
          font-weight: 700;
          color: #fff;
          line-height: 1.15;
          margin-bottom: 1.2rem;
          max-width: 720px;
        }

        .hero-meta {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.65);
          letter-spacing: 0.04em;
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }

        .hero-meta span::before {
          content: '·';
          margin-right: 1.5rem;
          opacity: 0.4;
        }
        .hero-meta span:first-child::before { display: none; }

        /* ── Layout ── */
        .article-layout {
          display: grid;
          grid-template-columns: 1fr min(680px, 90%) 1fr;
          row-gap: 0;
        }

        .article-layout > * {
          grid-column: 2;
        }

        .full-bleed {
          grid-column: 1 / -1;
        }

        /* ── Intro / dropcap ── */
        .intro-section {
          padding: 4rem 0 1rem;
        }

        .intro-section p:first-of-type::first-letter {
          font-family: 'Playfair Display', serif;
          font-size: 5rem;
          font-weight: 700;
          color: #1B5E6C;
          float: left;
          line-height: 0.78;
          margin: 0.08em 0.12em 0 0;
        }

        .intro-section p {
          font-size: 1.1rem;
          color: #3a3a3a;
          margin-bottom: 1.4rem;
        }

        /* ── Body sections ── */
        .body-section {
          padding: 0.5rem 0 1.5rem;
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }

        .body-section.visible {
          opacity: 1;
          transform: none;
        }

        .body-section p {
          font-size: 1.05rem;
          color: #3a3a3a;
          margin-bottom: 1.4rem;
          line-height: 1.85;
        }

        /* ── Gold divider ── */
        .gold-divider {
          grid-column: 2;
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 2rem 0;
          opacity: 0;
          transform: scaleX(0.6);
          transform-origin: left;
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .gold-divider.visible {
          opacity: 1;
          transform: scaleX(1);
        }

        .gold-divider::before, .gold-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #D4A54A;
          opacity: 0.5;
        }

        .gold-star {
          color: #D4A54A;
          font-size: 1rem;
          letter-spacing: 0.4em;
        }

        /* ── Pull Quote ── */
        .pull-quote {
          grid-column: 2;
          border-left: 3px solid #D4A54A;
          padding: 1.4rem 2rem;
          background: #f9f0e3;
          margin: 1.5rem 0;
          border-radius: 0 4px 4px 0;
          opacity: 0;
          transform: translateX(-20px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .pull-quote.visible {
          opacity: 1;
          transform: none;
        }

        .pull-quote p {
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-size: 1.25rem;
          color: #1B5E6C;
          line-height: 1.6;
        }

        /* ── In-line images ── */
        .inline-image-wrap {
          grid-column: 2;
          margin: 2rem 0;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .inline-image-wrap.visible {
          opacity: 1;
          transform: none;
        }

        .inline-image-wrap img {
          width: 100%;
          height: 380px;
          object-fit: cover;
          border-radius: 4px;
          display: block;
        }

        .inline-image-wrap figcaption {
          font-size: 0.78rem;
          color: #888;
          letter-spacing: 0.04em;
          margin-top: 0.6rem;
          text-align: center;
          font-style: italic;
        }

        /* ── Wide image (full bleed) ── */
        .wide-image-wrap {
          grid-column: 1 / -1;
          margin: 2.5rem 0;
          opacity: 0;
          transition: opacity 0.9s ease;
        }

        .wide-image-wrap.visible {
          opacity: 1;
        }

        .wide-image-wrap img {
          width: 100%;
          height: 440px;
          object-fit: contain;
          display: block;
        }

        .wide-image-wrap figcaption {
          text-align: center;
          font-size: 0.78rem;
          color: #888;
          font-style: italic;
          padding: 0.6rem 0 0;
          max-width: 600px;
          margin: 0 auto;
        }

        /* ── Section heading ── */
        .section-heading {
          font-family: 'Playfair Display', serif;
          font-size: 1.55rem;
          font-weight: 600;
          color: #1B5E6C;
          margin: 2rem 0 1rem;
          line-height: 1.3;
        }

        /* ── Closing card ── */
        .closing-card {
          grid-column: 2;
          background: #1B5E6C;
          color: #fff;
          border-radius: 6px;
          padding: 3rem 3.5rem;
          margin: 3rem 0;
          text-align: center;
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .closing-card.visible {
          opacity: 1;
          transform: none;
        }

        .closing-card h2 {
          font-family: 'Playfair Display', serif;
          font-size: 1.7rem;
          margin-bottom: 0.8rem;
          color: #fff;
        }

        .closing-card p {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.78);
          margin-bottom: 0.4rem;
        }

        .closing-card a {
          color: #D4A54A;
          text-decoration: none;
          font-weight: 500;
        }

        /* ── Footer bar ── */
        .blog-footer {
          grid-column: 1 / -1;
          background: #1a2a2a;
          color: rgba(255,255,255,0.5);
          text-align: center;
          padding: 1.8rem;
          font-size: 0.78rem;
          letter-spacing: 0.06em;
        }

        @media (max-width: 640px) {
          .hero { height: 85vh; }
          .hero-content { padding: 0 1.5rem 3rem; }
          .inline-image-wrap img { height: 230px; }
          .wide-image-wrap img { height: 260px; }
          .closing-card { padding: 2rem 1.5rem; }
        }
      `}</style>

      <div className="blog-wrap">
        {/* ── Hero ── */}
        <section className="hero">
          <img src={images.hero} alt="Ray of Hope Center for Autism launch" />
          <div className="hero-overlay" />
          <div className="hero-content">
            <span className="hero-tag">Center Launch · December 2025</span>
            <h1>A New Ray of Hope Begins at Electronics City</h1>
            <div className="hero-meta">
              <span>Ray of Hope Center for Autism</span>
              <span>13 December 2025</span>
              <span>RR Towers, Hosur Road</span>
            </div>
          </div>
        </section>

        {/* ── Article body ── */}
        <main className="article-layout">

          {/* Intro */}
          <div className="intro-section">
            <p>
              Ray of Hope Center for Autism (RHCA) marked a significant milestone with the launch of its new center at RR Towers — Hosur Road, opposite Hosa Road Metro Station — on 13 December 2025. This new location represents not just an expansion of physical space, but a deepening of purpose: to serve children and families with empathy, excellence, and Christ-like love.
            </p>
          </div>

          {/* Divider */}
          <div className="gold-divider" ref={addRef}>
            <span className="gold-star">✦ ✦ ✦</span>
          </div>

          {/* Prayer section */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">Committed in Prayer</h2>
            <p>
              The launch began in prayer as Dr Joy M George, Founder & Correspondent of Asian Christian Academy (ACA), committed the center into God's hands. His prayer set the tone for the entire event — one of thanksgiving for God's faithfulness, and trust in His guidance for every child, family, and professional who would walk through these doors.
            </p>
          </div>

          <figure className="inline-image-wrap" ref={addRef}>
            <img src={images.prayer} alt="Opening prayer at the center launch" />
            <figcaption>Dr Joy M George leading the center in an opening prayer of thanksgiving</figcaption>
          </figure>

          {/* Ribbon cutting */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">The Ceremonial Opening</h2>
            <p>
              The ceremonial ribbon cutting was done by Mr John Thayil, Honourable Board Member of ACA, formally opening the center and ushering in a new chapter for RHCA's mission in the Electronics City corridor.
            </p>
            <p>
              Guests were welcomed into the speech therapy room, where the launch program unfolded in a spirit of warmth and purpose. Keziah George, Center In-Charge of the new RHCA facility and host for the event, offered the welcome address — sharing the journey behind the birth of this center, an effort shaped by prayer, vision, and a clear calling to meet a growing need in the community.
            </p>
          </div>

          {/* Pull Quote 1 */}
          <blockquote className="pull-quote" ref={addRef}>
            <p>"{pullQuotes[0]}"</p>
          </blockquote>

          <figure className="wide-image-wrap" ref={addRef}>
            <img src={images.ribbonCutting} alt="Ribbon cutting ceremony" />
            <figcaption>The formal ribbon-cutting ceremony at RR Towers, Electronics City</figcaption>
          </figure>

          {/* Voices section */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">Voices of Purpose</h2>
            <p>
              The program was enriched by an address from Chella Pandian Pitchai, AVP & Global Head of DEI, Culture, Values & Belongingness at Biocon Biologics, who spoke about the role of institutions like RHCA in giving back to society. He highlighted how inclusive spaces play a vital role in creating dignity, opportunity, and meaningful support for children with diverse needs.
            </p>
            <p>
              A Scripture reading from Psalm 109, by Jerusha Biju, reminded everyone present of God's enduring goodness and faithfulness — an anchor for the work RHCA is called to do. The music team led by Albert then sang "All Glory Be to Christ," drawing the gathering into worship with meekness and humility.
            </p>
          </div>

          <figure className="inline-image-wrap" ref={addRef}>
            <img src={images.worship} alt="Worship and music during the launch event" />
            <figcaption>The music team led the gathering in worship, setting the spirit of the afternoon</figcaption>
          </figure>

          {/* Children section */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">For the Children, Above All</h2>
            <p>
              Rachel George, Director of RHCA, then shared the inspiration and motivation behind establishing this new center. Her address reflected a deep commitment to children and families, and a vision rooted in compassion, professional care, and hope.
            </p>
          </div>

          <figure className="inline-image-wrap" ref={addRef}>
            <img src={images.children} alt="Children receiving support and care" />
            <figcaption>The heart of RHCA — nurturing children with patience, love, and professional care</figcaption>
          </figure>

          {/* Pull Quote 2 */}
          <blockquote className="pull-quote" ref={addRef}>
            <p>"{pullQuotes[1]}"</p>
          </blockquote>

          <div className="body-section" ref={addRef}>
            <p>
              In his address, Dr Stephen George, President of ACA, spoke movingly about the foundation on which Ray of Hope stands. He shared how the love of Christ flows to us and through us, shaping empathy as one of the core pillars of RHCA. Reflecting on the life of Jesus, who identified with humanity and reached out to the marginalized, he reminded the audience that Ray of Hope exists not to generate profit, but to serve, love, and walk alongside those in need.
            </p>
          </div>

          {/* Divider */}
          <div className="gold-divider" ref={addRef}>
            <span className="gold-star">✦ ✦ ✦</span>
          </div>

          {/* Community section */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">A Community Gathered in Hope</h2>
            <p>
              As the program continued, Albert and team brought in the joy of the season by singing "Joy to the World" — a fitting reminder of God's greatest gift and the reason behind every act of service and compassion.
            </p>
            <p>
              The event concluded with a heartfelt vote of thanks by Gayathri Ravi, acknowledging all who contributed to making the new center a reality, followed by a closing prayer and benediction by Pastor Michael Jesudason, committing the journey ahead to God.
            </p>
          </div>

          <figure className="wide-image-wrap" ref={addRef}>
            <img src={images.community} alt="Community gathered at the center launch" />
            <figcaption>Families, guests, and the RHCA team united in hope on launch day</figcaption>
          </figure>

          {/* Closing reflection */}
          <div className="body-section" ref={addRef}>
            <h2 className="section-heading">A Defining Moment</h2>
            <p>
              The new RHCA Electronics City center stands as a testimony to what can be launched when vision, faith, and service come together. Ever since this journey started, there have been wonderful moments where God's goodness and love have flowed in and through the team.
            </p>
          </div>

          {/* Pull Quote 3 */}
          <blockquote className="pull-quote" ref={addRef}>
            <p>"{pullQuotes[2]}"</p>
          </blockquote>

          <figure className="inline-image-wrap" ref={addRef}>
            <img src={images.hope} alt="Symbol of hope and new beginnings" />
            <figcaption>By faith, looking back at this day — a defining moment for children and families in Electronics City</figcaption>
          </figure>

          {/* CTA card */}
          <div className="closing-card" ref={addRef}>
            <h2>Visit or Connect With Us</h2>
            <p>Ray of Hope Center for Autism · Electronics City</p>
            <p>RR Towers, Hosur Road, opposite Hosa Road Metro Station</p>
            <p style={{ marginTop: "1rem" }}>
              <a href="mailto:rayofhope@acaindia.org">rayofhope@acaindia.org</a>
              &nbsp;&nbsp;·&nbsp;&nbsp;
              <a href="tel:7305988924">7305988924</a>
            </p>
          </div>

          {/* Footer */}
          <footer className="blog-footer">
            © 2025 Ray of Hope Center for Autism · Asian Christian Academy · All rights reserved
          </footer>

        </main>
      </div>
    </>
  );
}
